import React from 'react';
import {
  Box,
  Typography,
  Button,
  Divider,
  BoxProps,
  SxProps,
  useTheme,
  useMediaQuery,
  TypographyProps,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';

import { Link, Params, useParams } from 'react-router-dom';
import { IGroupedTopic } from '../../../../store/SubTopicSlice';
import { ChipNew } from '../../Subject/components/SubTopicList';
import { getUrlConvertedString } from '../../../../helper';

export const getTopicUrl = (
  topicData: IGroupedTopic,
  params: Readonly<Params<string>>
): string => {
  const topicName = getUrlConvertedString(topicData.name);

  return `${process.env.PUBLIC_URL}/dashboard/${params.curriculum}/${params.subject}/${params.subjectLevel}/${params.subjectCode}/${topicName}/${topicData._id}`;
};

const topicBoxProps: BoxProps = {
  borderRadius: '10px',
  p: '10px',
};

const navButtonBoxProps: BoxProps = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  minWidth: 'fit-content',
  mb: 2,
  mt: 1,
};

const timeLineStyles: SxProps = {
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#E2E8F0',
    borderRadius: '5px',
    maxWidth: '260px',
    maxHeight: '35px',
  },
};

const timelineItemTypoProps: TypographyProps = {
  component: 'h2',
  fontSize: '16px',
};

interface SubTopicSectionProps {
  selectedTopic: IGroupedTopic;
  selectedTopicIndex: number;
  topicList: Array<IGroupedTopic>;
  subjectColor: string;
}

const SubTopicSection: React.FC<SubTopicSectionProps> = ({
  selectedTopic,
  selectedTopicIndex,
  topicList,
  subjectColor,
}) => {
  const params = useParams();
  const theme = useTheme();
  const belowMd = useMediaQuery(theme.breakpoints.down('md'));

  const pastPapers = selectedTopic.subTopics.filter((subTopic) =>
    subTopic.name.toLowerCase().includes('past paper')
  );
  const worksheets = selectedTopic.subTopics.filter((subTopic) =>
    subTopic.name.toLowerCase().includes('worksheet')
  );
  const flashcards = selectedTopic.subTopics.filter((subTopic) =>
    subTopic.name.toLowerCase().includes('flashcard')
  );
  const subTopics = selectedTopic.subTopics.filter(
    (subTopic) =>
      !pastPapers.includes(subTopic) &&
      !worksheets.includes(subTopic) &&
      !flashcards.includes(subTopic)
  );

  const handleChange = (id: string) => {
    const section = document.getElementById(id);

    if (section) {
      const offset = 80;
      const elementPosition =
        section.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  const getNextTopicUrl = () => {
    if (topicList.length && selectedTopicIndex < topicList.length - 1) {
      const nextTopic = topicList[selectedTopicIndex + 1];
      return getTopicUrl(nextTopic, params);
    }
    return '';
  };

  const getPreviousTopicUrl = () => {
    if (topicList.length && selectedTopicIndex > 0) {
      const previousTopic = topicList[selectedTopicIndex - 1];
      return getTopicUrl(previousTopic, params);
    }
    return '';
  };

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderRadius: '20px',
        p: 2,
        marginRight: !belowMd ? '15px' : null,
        marginBottom: belowMd ? '15px' : null,
        minHeight: { md: '50vh', lg: '80vh', xl: '80vh' },
      }}
    >
      <Box {...topicBoxProps} sx={{ backgroundColor: `${subjectColor}0A` }}>
        <Typography
          component="h1"
          fontSize="20px"
          letterSpacing="0.5px"
          fontWeight={550}
          align="center"
          color={subjectColor}
        >
          {selectedTopic?.name}
        </Typography>
      </Box>
      <Box {...navButtonBoxProps}>
        <Button
          replace
          component={Link}
          to={getPreviousTopicUrl()}
          disabled={selectedTopicIndex <= 0}
          sx={{ color: subjectColor, fontSize: '16px' }}
        >
          <KeyboardArrowLeft />
          Previous
        </Button>
        <Button
          replace
          component={Link}
          to={getNextTopicUrl()}
          disabled={selectedTopicIndex >= topicList.length - 1}
          sx={{ color: subjectColor, fontSize: '16px' }}
        >
          Next
          <KeyboardArrowRight />
        </Button>
      </Box>
      {!belowMd && (
        <>
          <Divider sx={{ border: '0.5px dashed #CBD5E1', mb: 2, mt: 3 }} />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'left',
              ml: { md: '45px', lg: '0px', xl: '5px' },
            }}
          >
            <Timeline
              sx={{
                ml: '-230px',
                '& .MuiTimelineContent-root': {
                  textAlign: 'left',
                },
              }}
            >
              {subTopics.length ? (
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot variant="outlined" />
                    {pastPapers.length ||
                    worksheets.length ||
                    flashcards.length ? (
                      <TimelineConnector />
                    ) : null}
                  </TimelineSeparator>
                  <TimelineContent
                    onClick={() => handleChange('subtopics')}
                    sx={timeLineStyles}
                  >
                    <Typography {...timelineItemTypoProps}>
                      Subtopics
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
              ) : null}
              {pastPapers.length ? (
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot variant="outlined" />
                    {worksheets.length || flashcards.length ? (
                      <TimelineConnector />
                    ) : null}
                  </TimelineSeparator>
                  <TimelineContent
                    onClick={() => handleChange('topical-past-papers')}
                    sx={timeLineStyles}
                  >
                    <Box display="flex" flexDirection="row">
                      <Typography {...timelineItemTypoProps}>
                        Topical past paper
                      </Typography>
                      <ChipNew />
                    </Box>
                  </TimelineContent>
                </TimelineItem>
              ) : null}
              {worksheets.length ? (
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot variant="outlined" />
                    {flashcards.length ? <TimelineConnector /> : null}
                  </TimelineSeparator>
                  <TimelineContent
                    onClick={() => handleChange('worksheets')}
                    sx={timeLineStyles}
                  >
                    <Typography {...timelineItemTypoProps}>
                      Worksheet
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
              ) : null}
              {flashcards.length ? (
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot variant="outlined" />
                  </TimelineSeparator>
                  <TimelineContent
                    onClick={() => handleChange('flashcards')}
                    sx={timeLineStyles}
                  >
                    <Typography {...timelineItemTypoProps}>
                      Flashcards
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
              ) : null}
            </Timeline>
          </Box>
          <Divider sx={{ border: '0.5px dashed #CBD5E1', mt: -1 }} />
        </>
      )}
    </Box>
  );
};

export default SubTopicSection;
